import { DatePipe, DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import {
  KI_SPERRTEXT_KEYS,
  KiAnfrage,
  KiPipelineAktionTyp,
  KiPipelineAktionVorlage,
  KiPipelineErgebnis,
  KiPipelineSicherheit,
} from '../../../models/ki.model';
import { PipelineFehlerBefund, PipelineFehlerKategorie, ResultStatus } from '../../../models/test-run.model';
import { KiAnfrageService } from '../../../services/ki-anfrage.service';
import { KiRunService } from '../../../services/ki-run.service';
import { KiStatusService } from '../../../services/ki-status.service';
import {
  istBekannteKategorie,
  PipeKategoriePillComponent,
} from '../../pipe-kategorie-pill/pipe-kategorie-pill.component';
import { KiBadgeComponent } from '../ki-badge/ki-badge.component';
import { KiButtonComponent, kiStatusSperre } from '../ki-button/ki-button.component';
import { KiErsteNutzungComponent } from '../ki-erste-nutzung/ki-erste-nutzung.component';
import { KiFehlerComponent } from '../ki-fehler/ki-fehler.component';
import { fokusVerloren } from '../ki-fokus';
import { KiGesendetComponent } from '../ki-gesendet/ki-gesendet.component';
import { KiLaeuftComponent } from '../ki-laeuft/ki-laeuft.component';

/** Kennung und Absprung eines am Durchlauf verknuepften Bugs; ohne bekannte Adresse kein Absprung. */
export interface KiPipelineVerknuepfterBug {
  key: string;
  url: string | null;
}

let naechsteId = 0;

const NICHT_FEHLGESCHLAGEN = 'ki.fehler.laufNichtFehlgeschlagen';

/** Je Ende des Laufs gibt es genau eines davon: Ergebnis, Fehler mit erneutem Versuch oder wieder den Button. */
const FOKUS_NACH_LAUF =
  '[data-testid="ki-pipeline-erneut-analysieren"], [data-testid="ki-fehler-erneut"], [data-testid="ki-button"]';

const SICHERHEITEN: readonly KiPipelineSicherheit[] = ['hoch', 'mittel', 'niedrig'];

interface KiPipelineAnzeige {
  anfrage: KiAnfrage;
  ergebnis: KiPipelineErgebnis;
  sicherheit: KiPipelineSicherheit | null;
  aktionTyp: KiPipelineAktionTyp;
  wiederkehrend: { anzahl: number; commit: string | null } | null;
}

/** Job und Schritt wie „build › npm run build“; ein unbekannter Teil fällt samt Trenner weg. */
function jobUndSchritt(job?: string | null, schritt?: string | null): string {
  return [job, schritt].filter((teil) => !!teil).join(' › ');
}

/** Nur Zusammenfassung und Ursache sind Pflicht; ohne sie gibt es nichts Lesbares zu zeigen. */
function alsErgebnis(wert: unknown): KiPipelineErgebnis | null {
  if (!wert || typeof wert !== 'object') return null;
  const ergebnis = wert as Partial<KiPipelineErgebnis>;
  if (typeof ergebnis.zusammenfassung !== 'string' || typeof ergebnis.ursache !== 'string') return null;
  return {
    ...ergebnis,
    logstellen: Array.isArray(ergebnis.logstellen) ? ergebnis.logstellen : [],
  } as KiPipelineErgebnis;
}

/**
 * Ursachen-Block eines gescheiterten Pipeline-Laufs (Design `page-ki.jsx:454-548`, `KiPipelineUrsache`):
 * Stufe 1 ohne KI, darunter Stufe 2 „Ursache erklären“. Ohne bekannte Kategorie zeigt er nichts:
 * erfolgreiche, laufende und Bestandslaeufe tragen keine. Stufe 2 braucht Projekt und Ausfuehrung.
 */
@Component({
  selector: 'app-ki-pipeline-ursache',
  standalone: true,
  imports: [
    DatePipe,
    KiBadgeComponent,
    KiButtonComponent,
    KiErsteNutzungComponent,
    KiFehlerComponent,
    KiGesendetComponent,
    KiLaeuftComponent,
    MatButtonModule,
    MatIconModule,
    PipeKategoriePillComponent,
    TranslatePipe,
  ],
  providers: [KiRunService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ki-pipeline-ursache.component.html',
  styleUrl: './ki-pipeline-ursache.component.scss',
})
export class KiPipelineUrsacheComponent {
  protected readonly run = inject(KiRunService);
  private readonly kiStatus = inject(KiStatusService);
  private readonly anfragen = inject(KiAnfrageService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly bereich = viewChild<ElementRef<HTMLElement>>('bereich');

  readonly befund = input.required<PipelineFehlerBefund>();
  /** Wohin „Im CI-System öffnen" fuehrt; ohne Adresse entfaellt der Link. */
  readonly ciUrl = input<string | null>(null);
  /** Status der Ausfuehrung; bei einer aussagekraeftigen Runde entscheidet er, wie sie zaehlt. */
  readonly status = input<ResultStatus | null>(null);
  readonly projectId = input<string | null>(null);
  /** Analysiert wird die angezeigte Ausfuehrung, nicht der ganze Testlauf. */
  readonly ausfuehrungId = input<string | null>(null);
  /** Die Seite bietet nur an, was sie ausfuehren kann; ohne Weg bliebe der Knopf ohne Wirkung. */
  readonly aktionAnbieten = input(false);
  readonly erneutStartenAnbieten = input(false);
  /**
   * Der am Durchlauf verknuepfte Bug: steht immer in Stufe 1, auch fuer Leser und ohne KI, damit er nach dem
   * Neuladen sichtbar bleibt; nach einer Analyse zusaetzlich anstelle der Aktion.
   */
  readonly verknuepfterBug = input<KiPipelineVerknuepfterBug | null>(null);
  /** Absprung aus dem Postfach (`?kiAnfrage`): das gespeicherte Ergebnis, ohne neue Anfrage. */
  readonly kiAnfrageId = input<string | null>(null);
  /** Empfohlene Aktion samt Text der Analyse, mit dem die Seite den Bug-Dialog vorbelegt; nur mit `aktionAnbieten`. */
  readonly aktionVorlage = output<KiPipelineAktionVorlage>();
  readonly erneutStarten = output<void>();

  protected readonly labelId = `kpu-label-${naechsteId++}`;
  protected readonly logstellenOffen = signal(false);
  private readonly gespeichert = signal<KiAnfrage | null>(null);
  /**
   * Lauf aus dem Block heraus gestartet: Der auslösende Knopf verschwindet samt Fokus. Der Block hält ihn, bis
   * Ergebnis oder Fehler da sind, und gibt ihn dann weiter; hat die Nutzerin ihn inzwischen woandershin gesetzt,
   * bleibt er dort.
   */
  private fokusFolgtLauf = false;

  constructor() {
    effect(() => {
      this.run.zustand();
      this.run.hinweis();
      untracked(() => {
        if (this.fokusFolgtLauf) this.fokusNachZustand();
      });
    });
    effect((onCleanup) => {
      const projectId = this.projectId();
      const ausfuehrungId = this.ausfuehrungId();
      const anfrageId = this.kiAnfrageId();
      this.gespeichert.set(null);
      if (!projectId || !ausfuehrungId || !anfrageId) return;
      const abfrage = this.anfragen.laden(projectId, anfrageId).subscribe({
        next: (anfrage) => {
          // Das Postfach meldet nur fertige Analysen; eine laufende oder gescheiterte ersetzt nicht den Button.
          if (
            anfrage.art === 'PIPELINE_ANALYSE' &&
            anfrage.kontextId === ausfuehrungId &&
            anfrage.status === 'FERTIG'
          ) {
            this.gespeichert.set(anfrage);
          }
        },
        error: () => undefined,
      });
      onCleanup(() => abfrage.unsubscribe());
    });
  }

  protected readonly kategorie = computed(() => {
    const kategorie = this.befund().fehlerKategorie;
    return istBekannteKategorie(kategorie) ? kategorie : null;
  });

  /** Ohne Angabe des Backends gilt wie an der Pill nur ein Testfehler als aussagekraeftig. */
  protected readonly aussagekraeftig = computed(
    () => this.befund().aussagekraeftig ?? this.kategorie() === 'TESTFEHLER',
  );

  /**
   * Eine aussagekraeftige Runde zaehlt nach ihren Ergebnissen: bestanden (F1, roter Schritt nach
   * gruenen Tests) oder fehlgeschlagen, auch bei Abbruch oder Runner-Fehler nach roten Tests. Ohne
   * Status entscheidet die Kategorie.
   */
  protected readonly zaehltAlsKey = computed(() => {
    if (!this.aussagekraeftig()) return 'ki.pipeline.ursache.zaehlt.nichtAusgefuehrt';
    const status = this.status();
    const fehlgeschlagen =
      status === 'FAILED' || status === 'BLOCKED' || (status !== 'PASSED' && this.kategorie() === 'TESTFEHLER');
    return fehlgeschlagen ? 'ki.pipeline.ursache.zaehlt.fehlgeschlagen' : 'ki.pipeline.ursache.zaehlt.bestanden';
  });

  protected readonly jobSchritt = computed(() => {
    const { fehlerJob, fehlerSchritt } = this.befund();
    return jobUndSchritt(fehlerJob, fehlerSchritt) || null;
  });

  protected readonly jobUndSchritt = jobUndSchritt;

  protected readonly kiMoeglich = computed(() => !!this.projectId() && !!this.ausfuehrungId());

  /** Bei Sperre steht statt der Erklaerung der Sperrtext (page-ki.jsx:489), waehrend des Ladens nicht. */
  protected readonly untertextKey = computed(() => {
    const projectId = this.projectId();
    if (!projectId) return 'ki.pipeline.erklaeren.untertext';
    const sperre = kiStatusSperre(this.kiStatus.status(projectId)(), 'PIPELINE_ANALYSE');
    return sperre?.sperrgrund ? KI_SPERRTEXT_KEYS[sperre.sperrgrund] : 'ki.pipeline.erklaeren.untertext';
  });

  /** Ein fertiger Lauf ohne lesbares Ergebnis zaehlt als Fehler; ein neuer Lauf verdraengt das gespeicherte. */
  protected readonly anzeige = computed<KiPipelineAnzeige | null>(() => {
    const zustand = this.run.zustand();
    const anfrage = zustand.art === 'fertig' ? zustand.anfrage : zustand.art === 'bereit' ? this.gespeichert() : null;
    const ergebnis = alsErgebnis(anfrage?.ergebnis);
    if (!anfrage || !ergebnis) return null;
    const wiederkehrend = ergebnis.wiederkehrend;
    return {
      anfrage,
      ergebnis,
      sicherheit: SICHERHEITEN.find((wert) => wert === ergebnis.sicherheit) ?? null,
      aktionTyp: ergebnis.aktionTyp ?? (this.kategorie() === 'BUILD' ? 'ENTWICKLUNG' : 'BUG'),
      wiederkehrend:
        typeof wiederkehrend?.anzahl === 'number'
          ? { anzahl: wiederkehrend.anzahl, commit: wiederkehrend.commit ? wiederkehrend.commit.slice(0, 7) : null }
          : null,
    };
  });

  protected readonly fehlerKey = computed(() => {
    const zustand = this.run.zustand();
    if (zustand.art === 'fehler') return zustand.textKey;
    return zustand.art === 'fertig' && !this.anzeige() ? 'ki.fehler.allgemein' : null;
  });

  /** Ein nicht fehlgeschlagener Lauf bleibt es auch beim zweiten Versuch. */
  protected readonly erneutSinnvoll = computed(() => this.fehlerKey() !== NICHT_FEHLGESCHLAGEN);

  protected aktionAusloesen({ aktionTyp, ergebnis }: KiPipelineAnzeige, kategorie: PipelineFehlerKategorie): void {
    this.aktionVorlage.emit({
      aktionTyp,
      kategorie,
      zusammenfassung: ergebnis.zusammenfassung,
      ursache: ergebnis.ursache,
    });
  }

  protected erklaeren(): void {
    const projectId = this.projectId();
    const ausfuehrungId = this.ausfuehrungId();
    if (!projectId || !ausfuehrungId) return;
    this.logstellenOffen.set(false);
    this.fokusFolgtLauf = this.fokusImBereich();
    this.run.starten({
      projectId,
      art: 'pipeline-analyse',
      kontextTyp: 'TEST_RUN',
      kontextId: ausfuehrungId,
    });
  }

  protected erneutVersuchen(): void {
    this.fokusFolgtLauf = this.fokusImBereich();
    this.run.erneut();
  }

  private fokusImBereich(): boolean {
    return this.bereich()?.nativeElement.contains(this.document.activeElement) ?? false;
  }

  private fokusNachZustand(): void {
    afterNextRender(
      () => {
        // Den Hinweis vor der ersten Nutzung führt sein Dialog selbst, samt Rückgabe beim Abbrechen.
        if (this.run.hinweis()) return;
        const bereich = this.bereich()?.nativeElement;
        const laeuft = this.run.zustand().art === 'laeuft';
        const geparkt = this.document.activeElement === bereich;
        if (!bereich || (!geparkt && !fokusVerloren(this.document))) {
          this.fokusFolgtLauf = false;
          return;
        }
        if (!laeuft) this.fokusFolgtLauf = false;
        const ziel = laeuft ? null : bereich.querySelector<HTMLElement>(FOKUS_NACH_LAUF);
        // Wer während des Laufs weiterscrollt, wird nicht zum Block zurückgeholt.
        (ziel ?? bereich).focus({ preventScroll: true });
      },
      { injector: this.injector },
    );
  }
}
